import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Check, Trash2, User, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { getAllComments, approveComment, deleteComment } from '../../firebase/firestore';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const realComments = await getAllComments();
        setComments(realComments);
      } catch (error) {
        console.error('Error fetching comments:', error);
        toast.error('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, []);

  const handleApprove = async (commentId) => {
    try {
      await approveComment(commentId);
      setComments(prev => prev.map(comment => (
        comment.id === commentId ? { ...comment, approved: true } : comment
      )));
      toast.success('Comment approved');
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('Failed to approve comment');
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteComment(commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const filteredComments = comments.filter(comment => {
    if (filter === 'pending') return !comment.approved;
    if (filter === 'approved') return comment.approved;
    return true;
  });

  if (loading) {
    return <LoadingSpinner text="Loading comments..." />;
  }

  return (
    <>
      <Helmet>
        <title>Manage Comments - Fastro Blog Admin</title>
        <meta name="description" content="Moderate and manage Fastro Blog comments" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Manage Comments
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Moderate and manage Fastro Blog comments
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  filter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                All Comments ({comments.length})
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  filter === 'pending'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Pending ({comments.filter(c => !c.approved).length})
              </button>
              <button
                onClick={() => setFilter('approved')}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  filter === 'approved'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Approved ({comments.filter(c => c.approved).length})
              </button>
            </div>
          </div>

          {/* Comments List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            {filteredComments.length === 0 ? (
              <div className="text-center py-16">
                <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No comments found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {filter === 'all' 
                    ? 'No comments have been posted yet.' 
                    : `No ${filter} comments found.`
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredComments.map((comment) => (
                  <div key={comment.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className="bg-primary-100 dark:bg-primary-900 rounded-full p-2 mr-3">
                            <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {comment.name}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {comment.email}
                            </p>
                          </div>
                        </div>
                        <div className="ml-11">
                          <p className="text-gray-700 dark:text-gray-300 mb-3">
                            {comment.message}
                          </p>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span className="mr-4">
                              {comment.createdAt && comment.createdAt.toDate ? format(comment.createdAt.toDate(), 'MMM dd, yyyy HH:mm') : ''}
                            </span>
                            <span className="text-primary-600 dark:text-primary-400">
                              on "{comment.postTitle ? comment.postTitle : comment.postId}"
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              comment.approved 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>
                              {comment.approved ? 'Approved' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {!comment.approved && (
                          <button
                            onClick={() => handleApprove(comment.id)}
                            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 p-2 rounded-md hover:bg-green-50 dark:hover:bg-green-900 transition-colors duration-200"
                            title="Approve Comment"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(comment.id)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-200"
                          title="Delete Comment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminComments;
